import { Box, Button, MenuItem, Select, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { tokens } from '../../theme';
import Header from '../../components/Header';

const DestinationReports = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [countries, setCountries] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const token = localStorage.getItem('token');

  const fetchCountries = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/country`, { headers: { token } });
      const data = await res.json();
      setCountries(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error fetching countries for destination reports', e);
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', pageSize);
      if (selectedCountryId) params.append('countryId', selectedCountryId);
      if (startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }

      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/reports/destinations?${params.toString()}`,
        {
          headers: { token },
        }
      );
      const data = await res.json();
      if (data.success) {
        const list = data.data || [];
        setRows(
          list.map((row, index) => ({
            ...row,
            serialNo: (page - 1) * pageSize + index + 1,
          }))
        );
        setTotalCount(data.pagination?.total || list.length || 0);
      } else {
        setRows([]);
        setTotalCount(0);
      }
    } catch (e) {
      console.error('Error fetching destination reports', e);
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, selectedCountryId, startDate, endDate]);

  const formatEuro = (value) =>
    `€ ${value != null && !Number.isNaN(Number(value)) ? Number(value).toFixed(2) : '0.00'}`;

  const columns = [
    {
      field: 'serialNo',
      headerName: 'Serial No',
      width: 90,
      sortable: false,
      filterable: false,
    },
    {
      field: 'countryName',
      headerName: 'Destination',
      flex: 1.1,
      minWidth: 140,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'totalPassengers',
      headerName: 'Passengers',
      flex: 0.7,
      minWidth: 100,
    },
    {
      field: 'totalGuides',
      headerName: 'No of Guides',
      flex: 0.75,
      minWidth: 110,
    },
    {
      field: 'collectedAmount',
      headerName: 'Collected Amount',
      flex: 0.9,
      minWidth: 140,
      valueFormatter: (params) => formatEuro(params.value),
    },
    {
      field: 'guideFee',
      headerName: 'Guide Fee',
      flex: 0.85,
      minWidth: 110,
      valueFormatter: (params) => formatEuro(params.value),
    },
    {
      field: 'toBeSettled',
      headerName: 'To Be Settled (4-5)',
      flex: 0.95,
      minWidth: 155,
      valueFormatter: (params) => formatEuro(params.value),
    },
    {
      field: 'totalAdditionalIncome',
      headerName: 'Additional Income',
      flex: 0.95,
      minWidth: 145,
      valueFormatter: (params) => formatEuro(params.value),
    },
    {
      field: 'destinationRelatedCosts',
      headerName: 'Destination Cost',
      flex: 0.95,
      minWidth: 140,
      valueFormatter: (params) => formatEuro(params.value),
    },
    {
      field: 'totalOtherIncome',
      headerName: 'Other Income',
      flex: 0.85,
      minWidth: 120,
      valueFormatter: (params) => formatEuro(params.value),
    },
    {
      field: 'totalOtherCost',
      headerName: 'Other Cost',
      flex: 0.85,
      minWidth: 110,
      valueFormatter: (params) => formatEuro(params.value),
    },
    {
      field: 'netSettlementAmount',
      headerName: 'Net Settlement Amount',
      flex: 1,
      minWidth: 165,
      valueFormatter: (params) => formatEuro(params.value),
    },
    {
      field: 'totalMileage',
      headerName: 'Mileage',
      flex: 0.75,
      minWidth: 100,
      valueFormatter: (params) =>
        params.value != null ? Number(params.value).toLocaleString() : '0',
    },
    {
      field: 'actions',
      headerName: 'View',
      sortable: false,
      filterable: false,
      width: 120,
      renderCell: (params) => {
        const dateQuery =
          startDate && endDate
            ? `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
            : '';
        return (
        <Link to={`/reports/destinations/${params.row.countryId}${dateQuery}`}>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[600],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.blueAccent[700] },
            }}
            variant="contained"
          >
            View
          </Button>
        </Link>
        );
      },
    },
  ];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap" mb={2}>
        <Header
          title="DESTINATION REPORTS"
          subtitle="Summary of bookings, guides, collections and settlement by destination."
        />
      </Box>

      {/* Filters */}
      <Box
        mb={2}
        p={2}
        display="flex"
        flexWrap="wrap"
        gap={2}
        sx={{ backgroundColor: colors.primary[400], borderRadius: '8px' }}
      >
        <Box minWidth={220}>
          <Typography variant="body2" color={colors.grey[200]} mb={0.5}>
            Destination
          </Typography>
          <Select
            fullWidth
            size="small"
            value={selectedCountryId}
            onChange={(e) => {
              setSelectedCountryId(e.target.value);
              setPage(1);
            }}
            displayEmpty
            sx={{ backgroundColor: colors.primary[500] }}
          >
            <MenuItem value="">
              <em>All Destinations</em>
            </MenuItem>
            {countries.map((c) => (
              <MenuItem key={c.CountryID} value={c.CountryID}>
                {c.CountryName}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box minWidth={180}>
          <Typography variant="body2" color={colors.grey[200]} mb={0.5}>
            Start Date
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ backgroundColor: colors.primary[500] }}
          />
        </Box>

        <Box minWidth={180}>
          <Typography variant="body2" color={colors.grey[200]} mb={0.5}>
            End Date
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ backgroundColor: colors.primary[500] }}
          />
        </Box>

        <Box alignSelf="flex-end">
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedCountryId('');
              setStartDate('');
              setEndDate('');
              setPage(1);
            }}
            sx={{ borderColor: colors.blueAccent[500], color: colors.blueAccent[500] }}
          >
            Clear Filters
          </Button>
        </Box>
      </Box>

      <Box
        m="10px 0 0 0"
        height="600px"
        sx={{
          width: '100%',
          overflowX: 'auto',
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .name-column--cell': { color: colors.greenAccent[300] },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.countryId}
          loading={loading}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 25, 50]}
          pagination
          paginationMode="server"
          page={page - 1}
          rowCount={totalCount}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(1);
          }}
        />
      </Box>
    </Box>
  );
};

export default DestinationReports;

